<div
  class="ams-chatbot-panel"
  [class.ams-chatbot-panel--open]="open()"
  [class.ams-chatbot-panel--resizing]="isResizing()"
  [style.width.px]="effectiveWidth()"
  [attr.inert]="open() ? null : ''"
  role="complementary"
  aria-label="Membora AI Assistant"
>
  <div class="ams-chatbot-panel__resize-handle" (mousedown)="onResizeHandleMouseDown($event)">
    <span class="ams-chatbot-panel__resize-grip"></span>
  </div>

  <div class="ams-chatbot-panel__inner">
    <div class="ams-chatbot__header-bar">
      <div class="ams-chatbot__title-block">
        <h2 class="ams-chatbot__title">{{ title() }}</h2>
        @if (contextLabel()) {
          <div class="ams-chatbot__context-label">{{ contextLabel() }}</div>
        }
      </div>
      <div class="ams-chatbot__header-slot">
        <div class="ams-chatbot__header-actions">
          <button
            class="ams-chatbot__header-btn"
            type="button"
            [class.ams-chatbot__header-btn--active]="showHistory()"
            (click)="toggleHistory()"
            amsTooltip="Chat history"
            amsTooltipPosition="bottom"
            aria-label="Chat history"
          >
            <fa-icon [icon]="faClockRotateLeft" />
          </button>
          <button
            class="ams-chatbot__header-btn"
            type="button"
            (click)="newChat()"
            amsTooltip="New chat"
            amsTooltipPosition="bottom"
            aria-label="New chat"
          >
            <fa-icon [icon]="faPlus" />
          </button>
        </div>
      </div>
      <button class="ams-chatbot__close-btn" type="button" (click)="close()" aria-label="Close">
        <fa-icon [icon]="faXmark" />
      </button>
    </div>

    <div class="ams-chatbot__body">
      @if (sendDisabledReason(); as reason) {
        <div class="ams-chatbot__blocked" role="status">
          <fa-icon [icon]="faTriangleExclamation" />
          <span>{{ reason }}</span>
        </div>
      }

      @if (error()) {
        <div class="ams-chatbot__error">
          <span class="ams-chatbot__error-text">{{ error() }}</span>
          <button class="ams-chatbot__error-retry" type="button" (click)="emitRetry()">
            <fa-icon [icon]="faRotateRight" />
            <span>Retry</span>
          </button>
        </div>
      }

      @if (showHistory()) {
        <div class="ams-chatbot__history">
          <div class="ams-chatbot__history-header">
            <button class="ams-chatbot__history-back" type="button" (click)="toggleHistory()">
              <fa-icon [icon]="faArrowLeft" /> Back to chat
            </button>
            <h3 class="ams-chatbot__history-title">Chat History</h3>
          </div>
          @if (history().length === 0) {
            <p class="ams-chatbot__history-empty">No previous chats yet.</p>
          } @else {
            @if (isHistoryFull()) {
              <div class="ams-chatbot__history-banner">
                <fa-icon [icon]="faTriangleExclamation" />
                <span>Your chat history is full. Starting a new chat will delete the oldest one.</span>
              </div>
            }
            <div class="ams-chatbot__history-list">
              @for (session of history(); track session.id) {
                <div class="ams-chatbot__history-item-row">
                  <button class="ams-chatbot__history-item" type="button" (click)="loadSession(session)">
                    <span class="ams-chatbot__history-item-preview">{{ sessionPreview(session) }}</span>
                    <span class="ams-chatbot__history-item-meta">
                      {{ session.startedAt | date: 'MMM d, h:mm a' }}
                    </span>
                  </button>
                  <button
                    class="ams-chatbot__history-item-delete"
                    type="button"
                    title="Delete chat"
                    (click)="requestDeleteSession(session, $event)"
                  >
                    <fa-icon [icon]="faTrash" />
                  </button>
                </div>
              }
            </div>
          }
        </div>
      } @else {
        @if (displayMessages().length === 0) {
          <div class="ams-chatbot__empty">
            <fa-icon [icon]="faRobot" class="ams-chatbot__empty-icon" />
            <h3 class="ams-chatbot__empty-title">{{ emptyTitle() }}</h3>
            <p class="ams-chatbot__empty-sub">
              {{ emptySubtitle() }}
            </p>
            @if (emptySuggestions().length > 0) {
              <div class="ams-chatbot__suggestions">
                @for (suggestion of emptySuggestions(); track suggestion.label) {
                  <button class="ams-chatbot__suggestion" type="button" (click)="sendSuggestion(suggestion)">
                    {{ suggestion.label }}
                  </button>
                }
              </div>
            }
          </div>
        } @else {
          <div class="ams-chatbot__messages" #messagesContainer>
            @for (message of displayMessages(); track message.id) {
              <ams-chatbot-message
                [message]="message"
                [showCitations]="showCitations()"
                [slotSelections]="slotSelectionsForMessage(message.id)"
                (sendSuggestion)="sendSuggestion($event)"
                (navigateTo)="navigationSelected.emit($event)"
                (confirmAction)="actionConfirmed.emit($event)"
                (dismissAction)="actionDismissed.emit($event)"
                (slotChipToggled)="emitSlotChipToggle(message.id, $event)"
              />
            }
            <div #messagesEnd></div>
          </div>
        }
      }
    </div>

    @if (!showHistory()) {
      <div class="ams-chatbot__footer">
        <div
          class="ams-chatbot__input-wrap"
          [class.ams-chatbot__input-wrap--focused]="inputFocused"
        >
          <textarea
            #inputEl
            class="ams-chatbot__input"
            [placeholder]="sendDisabledReason() ? sendDisabledReason()! : placeholder()"
            rows="1"
            [(ngModel)]="draft"
            [disabled]="isLoading() || !!sendDisabledReason()"
            (keydown)="onKeydown($event)"
            (input)="autoResize()"
            (focus)="inputFocused = true"
            (blur)="inputFocused = false"
          ></textarea>
          <button
            class="ams-chatbot__send"
            type="button"
            [disabled]="!draft.trim() || isLoading() || !!sendDisabledReason()"
            (click)="sendMessage()"
            aria-label="Send"
          >
            <fa-icon [icon]="faPaperPlane" />
          </button>
        </div>

        @if (usage(); as u) {
          <div class="ams-chatbot__usage">
            <div
              class="ams-chatbot__usage-item"
              [amsTooltip]="u.creditsEnabled
                ? (u.creditsCap === null
                    ? 'Unlimited AI credits on your plan'
                    : u.creditsUsed + ' of ' + u.creditsCap + ' monthly AI credits used')
                : 'AI is not included in your current plan'"
              amsTooltipPosition="top"
            >
              <fa-icon [icon]="faBolt" />
              @if (!u.creditsEnabled) {
                <span class="ams-chatbot__usage-text">AI off</span>
              } @else if (u.creditsCap === null) {
                <span class="ams-chatbot__usage-text">Unlimited</span>
              } @else {
                <span class="ams-chatbot__usage-text">{{ u.creditsUsed }} / {{ u.creditsCap }}</span>
                <span class="ams-chatbot__usage-bar">
                  <span class="ams-chatbot__usage-bar-fill" [style.width.%]="creditPercent()"></span>
                </span>
              }
            </div>
            <div
              class="ams-chatbot__usage-item"
              [amsTooltip]="u.sessionsUsed + ' of ' + u.sessionsCap + ' saved chats'"
              amsTooltipPosition="top"
            >
              <fa-icon [icon]="faComments" />
              <span class="ams-chatbot__usage-text">{{ u.sessionsUsed }} / {{ u.sessionsCap }}</span>
            </div>
          </div>
        }

        <p class="ams-chatbot__disclaimer">{{ disclaimer() }}</p>
      </div>
    }
  </div>
</div>
